/**
 * Version checker utility to detect when a new version of the app is deployed
 * and prompt users to refresh to avoid serving stale cached content
 */

const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

export class VersionChecker {
  private static checkInterval: NodeJS.Timeout | null = null;
  private static currentVersion: string = APP_VERSION;

  /**
   * Start periodic version checking
   */
  static startVersionCheck(onNewVersion?: () => void): void {
    // Initial check
    this.checkVersion(onNewVersion);

    // Periodic checks
    this.checkInterval = setInterval(() => {
      this.checkVersion(onNewVersion);
    }, VERSION_CHECK_INTERVAL);

    console.log('🔍 Version checker started. Current version:', this.currentVersion);
  }

  /**
   * Stop version checking
   */
  static stopVersionCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('⏹️ Version checker stopped');
    }
  }

  /**
   * Check if a new version is available
   */
  private static async checkVersion(onNewVersion?: () => void): Promise<void> {
    try {
      // Fetch the HTML file with cache-busting query parameter
      const response = await fetch(`/index.html?t=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      const etag = response.headers.get('ETag');
      const lastModified = response.headers.get('Last-Modified');

      // Use ETag or Last-Modified as version indicator
      const serverVersion = etag || lastModified || this.currentVersion;

      // Check if version changed
      if (serverVersion !== this.currentVersion && this.currentVersion !== APP_VERSION) {
        console.log('🆕 New version detected!', {
          current: this.currentVersion,
          new: serverVersion
        });

        if (onNewVersion) {
          onNewVersion();
        } else {
          this.promptUserToRefresh();
        }
      }

      // Update current version
      this.currentVersion = serverVersion;
    } catch (error) {
      console.warn('⚠️ Version check failed:', error);
      // Don't show errors to user - this is a background check
    }
  }

  /**
   * Prompt user to refresh the page
   */
  private static promptUserToRefresh(): void {
    const shouldRefresh = window.confirm(
      '🆕 A new version of ShipmentCoCaptain is available!\n\n' +
      'Would you like to refresh now to get the latest features and fixes?'
    );

    if (shouldRefresh) {
      // Hard reload to bypass cache
      window.location.reload();
    }
  }

  /**
   * Force a hard reload (bypasses all caches)
   */
  static forceReload(): void {
    console.log('🔄 Forcing hard reload...');
    
    // Clear service worker caches if present
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }

    // Clear any localStorage version indicators
    localStorage.removeItem('app_version');
    
    // Hard reload
    window.location.reload();
  }

  /**
   * Get current app version
   */
  static getCurrentVersion(): string {
    return this.currentVersion;
  }
}

export default VersionChecker;
